import React, {useState, useCallback, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {SVGICONS} from '@ey/icons';
import styled from 'styled-components';
import {labels, currentResource} from '../../../../../util/utils';
import {RelatedTasksDropdown} from '@ey/canvascoreservices';
import TaskBadgeAndName from '../../../TaskBadgeAndName/TaskBadgeAndName';
import EllipsesControl from '../../../EllipsesControl/EllipsesControl';
import Utility from '../../../../../util/utilityfunctions';
import MotifIcon from '@ey/motif-react-wrapper/Icon';
import {IconoirClipboardCheck} from '@ey-xd/motif-icon';

export default function RelatedTasksIcon({relatedTasks = [], documentId, isUploadLoading = false}) {
	const count = useMemo(() => relatedTasks.length, [relatedTasks]);
	const title = useMemo(() => labels?.relatedTasksCount?.replaceAll('{0}', count), [count]);

	const engagement = useSelector((store) => store.engagement);
	const [isOpen, setIsOpen] = useState(false);

	const sortedTasks = useMemo(() => relatedTasks.sort((a, b) => a.taskName?.localeCompare(b.taskName)), [relatedTasks]);

	const hideDropdownContent = () => {
		setIsOpen(false);
	};

	const onDropdownClick = (e) => {
		e.target.className === 'motif-dropdown-trigger' ||
		(e.target.classList['value'].includes('motif-dropdown-portal') &&
			e.target.classList['value'].includes('relatedTaskDropdown'))
			? setIsOpen(false)
			: '';
	};

	const navigateToTask = (taskId) => {
		const url = Utility.getTaskUrl(
			taskId,
			undefined,
			engagement,
			undefined,
			undefined,
			undefined,
			undefined,
			documentId
		);

		window.open(url, '_blank');
	};

	const disableCount = useMemo(() => {
		return count === 0 || isUploadLoading;
	}, [count, isUploadLoading]);

	const renderTaskNameRow = useCallback(
		(task) => {
			return (
				<section id={`related_${task.taskId}`} className="TaskBadgeSection StyledTaskDrawerSummoner">
					<TaskBadgeAndName
						key={task.taskId}
						taskId={task.taskId}
						taskTypeId={task.taskTypeId}
						taskName={task.taskName}
						isEp={task.isEPSignoffRequired || task.isEPSignoffUserRequired}
						isEq={task.isEQRSignoffRequired || task.isEQRSignoffUserRequired}
						isEpEqr={
							(task.isEPSignoffRequired || task.isEPSignoffUserRequired) &&
							(task.isEQRSignoffRequired || task.isEQRSignoffUserRequired)
						}
						engagement={engagement}
						taskTypeBadges={currentResource.taskTypeBadge}
						linesBeforeEllipse={2}
						onClick={() => {
							navigateToTask(task.taskId);
							hideDropdownContent();
						}}
						showLink={false}
						noBackground
						noBorderPadding
						isEllipsed
					/>
				</section>
			);
		},
		[sortedTasks]
	);

	return (
		<section className="relatedTasksSection">
			{sortedTasks?.length > 1 ? (
				<RelatedTasksDropdown
					tasks={sortedTasks}
					relatedTasksCount={count}
					onClick={onDropdownClick}
					onClickOutside={hideDropdownContent}
					relatedTasksLabel={labels.relatedTasksTitle}
					isOpen={isOpen}
					onTriggerClick={() => setIsOpen(!isOpen)}
					triggerTitle={title}
					hideOnScroll={hideDropdownContent}
					taskTypeBadges={currentResource.taskTypeBadge}
					onCloseButtonClick={hideDropdownContent}
					closeButtonTitle={labels.closeLabel}
					showCloseButton
					renderTaskName={renderTaskNameRow}
					showLoader={false}
				/>
			) : (
				<StyledRelatedTasksIcon
					onClick={() => {
						if (disableCount) {
							return;
						}
						navigateToTask(sortedTasks[0]?.taskId);
					}}
					className="StyledRelatedTasksIcon"
					title={title}
				>
					<MotifIcon
						className={`medium ${disableCount && 'disabled-icon'}`}
						src={IconoirClipboardCheck}
						title={title}
						disabled={disableCount}
					/>
					<EllipsesControl content={count} tooltip={count} noOfLines={1} disabled={disableCount} />
				</StyledRelatedTasksIcon>
			)}
		</section>
	);
}

const StyledRelatedTasksIcon = styled.section`
	display: flex;
	flex-wrap: wrap;
	width: var(--px-25);
	justify-content: center;
	font-size: var(--px-11) !important;
	.disabled-icon {
		cursor: not-allowed;
	}
	.motif-icon {
		line-height: var(--px-10);
		svg {
			width: var(--px-18) !important;
			height: var(--px-18) !important;
			cursor: pointer;
			color: var(--neutrals-900);
		}
	}
	.ellipses {
		font-size: var(--px-11) !important;
		justify-content: center;
		cursor: pointer;
		line-height: normal;
	}
`;
